import React, {createContext, useContext, useEffect, useState} from 'react';
import moment from "moment";
import {useSelector} from "react-redux";
import {RootState} from "../../../reducers";
import {useActions} from "../../../actions";
import * as PaymentActions from "../../../actions/payment";
import {ATLANTIC_HEALTH, Frequency} from "../../../constants"
import {isDebug} from "../../../utils/commonUtils";


const PolicyContext = createContext({});


async function getPolicyDetails(payment: any, clientData: any) {
    const {policyNumber, contributionAmount, fundingDate, policyNumberCarrier, trancheNumber, carrier, clientName, clientUsername, bankAmount, employerContribution, paymentFrequency} = clientData;
    if (policyNumber && contributionAmount && fundingDate) {
        const paymentPayload = {
            "policyNumber": policyNumber,
            "policyNumberCarrier": policyNumberCarrier,
            "trancheNumber": trancheNumber + "",
            "carrier": carrier,
            "clientName": clientName,
            "clientUsername": clientUsername,
            "clientAnnualAmount": contributionAmount,
            "bankAnnualAmount": bankAmount,
            "employerAnnualAmount": employerContribution || 0,
            "trustFeeAnnualAmount": 1350,
            "clientPaymentYears": [1, 2, 3, 4, 5],
            "bankPaymentYears": [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            "employerPaymentYears": [1, 2, 3, 4, 5],
            "policyStartDate": moment(fundingDate).format("YYYY-MM-DD"),
            "paymentFrequency": paymentFrequency,
            isDebug: isDebug(),
        }
        return await payment.policyDetails(paymentPayload);
    } else {
        return;
    }
}

// Create a provider component
export const PolicyContextProvider = (props: any) => {
    const {clientData = {}} = props;
    const [policy, setPolicy] = useState(null);
    const clientDetail = useSelector((state: RootState) => state.profile);
    let contributionAmount: number = 0;
    let bankAmount: number = 0;
    let policyNumber: string;
    let fundingDate: string;
    let policyNumberCarrier: string | undefined;
    let trancheNumber: string;
    let carrier: string;
    let clientName: string;
    let clientUsername: string;
    let employerContribution: number = 0;
    const isGWTParent = (clientData && clientData.isGWTParent) || false;
    const clientPolicy = clientData && clientData.clientPolicies && clientData.clientPolicies.length > 0 && clientData.clientPolicies[0] || clientData && clientData.clientPolicy;

    let paymentFrequency: string;
    if (clientPolicy && clientPolicy.issuedContribution) {
        employerContribution = clientPolicy.employerContribution;
        if (ATLANTIC_HEALTH.includes(clientPolicy.company && clientPolicy.company.companyLinkUrl)) {
            contributionAmount = clientPolicy.issuedContribution - clientPolicy.employerContribution;
        } else {
            contributionAmount = clientPolicy.issuedContribution;
        }

        if (clientPolicy.issuedContribution == 0 && employerContribution) {
            bankAmount = (clientPolicy.annualPremium - employerContribution) + 1350;
        } else {
            bankAmount = (clientPolicy.annualPremium - clientPolicy.issuedContribution) + 1350;
        }
        policyNumber = clientPolicy.id;
        fundingDate = clientPolicy.tranche && clientPolicy.tranche.projectFundingDate;
        trancheNumber = clientPolicy.tranche && clientPolicy.tranche.trancheNumber || "";
        policyNumberCarrier = clientPolicy && clientData.policyNumber || "";
        paymentFrequency = clientPolicy.paymentFrequency || "annual";
        carrier = clientPolicy.carrier && clientPolicy.carrier.carrierName || "";
        clientName = clientData.name;
        clientUsername = clientData.email;
    } else {
        if (clientDetail && clientDetail.client) {
            const clientPolicy:any = clientDetail && clientDetail.client && clientDetail.client.clientPolicies && clientDetail.client.clientPolicies.length > 0 && clientDetail.client.clientPolicies[0] || {};
            const issuedContribution = clientPolicy.issuedContribution || 0;
            employerContribution = clientPolicy.employerContribution || 0;
            const annualPremium = clientPolicy.annualPremium || 0;
            if (ATLANTIC_HEALTH.includes(clientPolicy && clientPolicy.company && clientPolicy.company.companyLinkUrl || "")) {
                contributionAmount = issuedContribution - employerContribution;
            } else {
                contributionAmount = issuedContribution;
            }
            //Special Case if employer is payment full amount
            if (issuedContribution == 0 && employerContribution) {
                bankAmount = annualPremium - employerContribution;
            } else {
                bankAmount = (annualPremium - issuedContribution) + 1350;
            }
            policyNumber = clientPolicy && clientPolicy.id || "";
            fundingDate = clientPolicy.tranche && clientPolicy.tranche.projectFundingDate;
            trancheNumber = clientPolicy.tranche && clientPolicy.tranche.trancheNumber || "";
            policyNumberCarrier = clientPolicy && clientPolicy.policyNumber || "";
            paymentFrequency = clientPolicy.paymentFrequency || "annual";
            carrier = clientPolicy.carrier && clientPolicy.carrier.carrierName || "";
            clientName = clientDetail.firstName + ' ' + clientDetail.lastName;
            clientUsername = clientDetail.email;
        }
    }

    // NIWD-221 sachin
    // @ts-ignore
    paymentFrequency = !!paymentFrequency && paymentFrequency === "monthly" ? Frequency.Monthly : Frequency.Annual;

    const payment = useActions(PaymentActions);
    useEffect(() => {
        (async () => {
            if (contributionAmount && policyNumber) {
                let policyDetails = await getPolicyDetails(payment, {contributionAmount, policyNumber, fundingDate, policyNumberCarrier, trancheNumber, carrier, clientName, clientUsername, bankAmount, employerContribution, paymentFrequency});
                if (policyDetails) {
                    Object.assign(policyDetails, {isGWTParent});
                    setPolicy(policyDetails);
                }
            }
        })();

        return () => {
            // this now gets called when the component unmounts
        };
    }, []);
    const updatePolicy = (newPolicy: any) => {
        Object.assign(newPolicy, {isGWTParent});
        setPolicy(newPolicy);
    };

    const refetchPolicy = async () => {
        let policyDetails = await getPolicyDetails(payment, {contributionAmount, policyNumber, fundingDate, policyNumberCarrier, trancheNumber, carrier, clientName, clientUsername, bankAmount, employerContribution, paymentFrequency});
        if (policyDetails) {
            Object.assign(policyDetails, {isGWTParent});
            setPolicy(policyDetails);
        }
    };


    isDebug() && console.info({clientData, clientDetail})

    return (
        <PolicyContext.Provider value={{ policy, updatePolicy, refetchPolicy }}>
            {
                !!policy && isDebug() &&
                    <pre>{JSON.stringify(policy)}</pre>
            }
            {props.children}
        </PolicyContext.Provider>
    );
};

export const usePolicyContext = () => {
    const context: any = useContext(PolicyContext);
    if (!context) {
        throw new Error('usePolicyContext must be used within a PolicyContextProvider');
    }
    if (context.loading) {
        return "Loading.."
    }
    return context;
};